"use client";

import { usePointerInteractionMedia } from "@/components/feature/pointer-interaction-media";
import { useTheaterOverlayFrozen } from "@/components/feature/theater-overlay-freeze";
import { type MouseEvent, useCallback, useEffect, useRef } from "react";

type EdgeHoverTransformOptions = {
  /** Max translation in pixels toward the hovered edge. */
  maxShiftPx?: number;
  /** Max scale stretch toward the hovered edge (e.g. 0.03 = 3%). */
  maxStretch?: number;
  /**
   * Inner dead zone (0–1) along each axis. No effect until the cursor passes this
   * distance from center toward an edge.
   */
  deadZone?: number;
  /**
   * Exponent applied to edge proximity (≥ 1). Higher values concentrate stretch
   * closer to the outer edge.
   */
  edgePower?: number;
  /** Per-frame interpolation when stretching toward an edge (0–1). Higher = snappier. */
  expandFactor?: number;
  /** Per-frame interpolation when contracting (0–1). Lower = silkier. */
  contractFactor?: number;
};

type Weights = { wx: number; wy: number };

const SETTLE_EPSILON = 0.001;

function smoothstep(t: number): number {
  const clamped = Math.max(0, Math.min(1, t));
  return clamped * clamped * (3 - 2 * clamped);
}

function edgeWeight(coord: number, deadZone: number, edgePower: number): number {
  const abs = Math.abs(coord);
  if (abs <= deadZone) return 0;
  const t = (abs - deadZone) / (1 - deadZone);
  return smoothstep(t ** edgePower) * Math.sign(coord);
}

function weightsFromCursor(nx: number, ny: number, deadZone: number, edgePower: number): Weights {
  return {
    wx: edgeWeight(nx, deadZone, edgePower),
    wy: edgeWeight(ny, deadZone, edgePower),
  };
}

function buildTransformFromWeights(
  wx: number,
  wy: number,
  maxShiftPx: number,
  maxStretch: number
): string {
  if (wx === 0 && wy === 0) return "";

  const tx = wx * maxShiftPx;
  const ty = wy * maxShiftPx;
  const sx = 1 + Math.abs(wx) * maxStretch;
  const sy = 1 + Math.abs(wy) * maxStretch;
  return `translate3d(${tx}px, ${ty}px, 0) scale(${sx}, ${sy})`;
}

function lerp(current: number, target: number, factor: number): number {
  return current + (target - current) * factor;
}

function isExpanding(current: number, target: number): boolean {
  return Math.abs(target) > Math.abs(current) + SETTLE_EPSILON;
}

/** Stretching eases in quickly; contracting eases out more gradually. */
function stepAxis(
  current: number,
  target: number,
  expandFactor: number,
  contractFactor: number
): number {
  if (Math.abs(target - current) < SETTLE_EPSILON) return target;
  const factor = isExpanding(current, target) ? expandFactor : contractFactor;
  const next = lerp(current, target, factor);
  if (Math.abs(target - next) < SETTLE_EPSILON) return target;
  return next;
}

function isSettled(current: Weights, target: Weights): boolean {
  return (
    Math.abs(target.wx - current.wx) < SETTLE_EPSILON &&
    Math.abs(target.wy - current.wy) < SETTLE_EPSILON
  );
}

function stepWeights(
  current: Weights,
  target: Weights,
  expandFactor: number,
  contractFactor: number
): Weights {
  return {
    wx: stepAxis(current.wx, target.wx, expandFactor, contractFactor),
    wy: stepAxis(current.wy, target.wy, expandFactor, contractFactor),
  };
}

export function useEdgeHoverTransform({
  maxShiftPx = 6,
  maxStretch = 0.035,
  deadZone = 0.05,
  edgePower = 1.5,
  expandFactor = 0.22,
  contractFactor = 0.11,
}: EdgeHoverTransformOptions = {}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const isHoveringRef = useRef(false);
  const targetRef = useRef<Weights>({ wx: 0, wy: 0 });
  const currentRef = useRef<Weights>({ wx: 0, wy: 0 });

  const { canHover, hasPointerFine, prefersReducedMotion } = usePointerInteractionMedia();
  const overlayFrozen = useTheaterOverlayFrozen();
  const overlayFrozenRef = useRef(overlayFrozen);
  overlayFrozenRef.current = overlayFrozen;
  const enabled = canHover && hasPointerFine && !prefersReducedMotion && !overlayFrozen;

  const applyCurrentTransform = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.transition = "none";
    const { wx, wy } = currentRef.current;
    el.style.transform = buildTransformFromWeights(wx, wy, maxShiftPx, maxStretch);
  }, [maxShiftPx, maxStretch]);

  const clearTransform = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.style.transition = "";
    el.style.transform = "";
    // Release the compositor layer hint while idle so large grids don't hold
    // dozens of permanent layers (matters on low-end GPUs / Chromebooks).
    el.style.willChange = "";
  }, []);

  const stopAnimation = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const advanceWeights = useCallback(() => {
    const target = targetRef.current;
    currentRef.current = stepWeights(currentRef.current, target, expandFactor, contractFactor);
    applyCurrentTransform();
    return isSettled(currentRef.current, target);
  }, [applyCurrentTransform, contractFactor, expandFactor]);

  const tick = useCallback(() => {
    if (overlayFrozenRef.current) {
      rafRef.current = null;
      clearTransform();
      currentRef.current = { wx: 0, wy: 0 };
      return;
    }
    const settled = advanceWeights();
    if (settled) {
      rafRef.current = null;

      if (!isHoveringRef.current) {
        clearTransform();
        currentRef.current = { wx: 0, wy: 0 };
      }
      return;
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [advanceWeights, clearTransform]);

  const startAnimation = useCallback(() => {
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  const onMouseEnter = useCallback(() => {
    if (!enabled) return;
    isHoveringRef.current = true;
    const el = containerRef.current;
    if (el) {
      el.style.transition = "none";
      el.style.willChange = "transform";
    }
  }, [enabled]);

  const onMouseMove = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!enabled) return;
      const rect = e.currentTarget.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;

      const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      targetRef.current = weightsFromCursor(nx, ny, deadZone, edgePower);

      const settled = advanceWeights();
      if (!settled) startAnimation();
    },
    [advanceWeights, deadZone, edgePower, enabled, startAnimation]
  );

  const onMouseLeave = useCallback(() => {
    if (!enabled) return;
    isHoveringRef.current = false;
    targetRef.current = { wx: 0, wy: 0 };
    stopAnimation();
    rafRef.current = requestAnimationFrame(tick);
  }, [enabled, stopAnimation, tick]);

  useEffect(() => {
    if (!overlayFrozen) return;
    isHoveringRef.current = false;
    targetRef.current = { wx: 0, wy: 0 };
    stopAnimation();
    clearTransform();
    currentRef.current = { wx: 0, wy: 0 };
  }, [overlayFrozen, stopAnimation, clearTransform]);

  useEffect(() => {
    return () => {
      stopAnimation();
    };
  }, [stopAnimation]);

  return {
    enabled,
    containerRef,
    containerHandlers: enabled ? { onMouseEnter, onMouseMove, onMouseLeave } : {},
  };
}
